import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { X } from 'lucide-react';
import {
  STATIC_DELIVERY_DRIVERS,
  getDriverDisplayName,
  getDriverStatusLabel,
} from './staticDeliveryDrivers';
import './AssignDriverModal.scss';

function getInitials(firstName, lastName) {
  const a = String(firstName ?? '').trim()[0] ?? '';
  const b = String(lastName ?? '').trim()[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

export function AssignDriverModal({ open, orderNo, reassign = false, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedId(null);
    }
  }, [open]);

  const filteredDrivers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STATIC_DELIVERY_DRIVERS;
    return STATIC_DELIVERY_DRIVERS.filter((d) => {
      const name = getDriverDisplayName(d).toLowerCase();
      const phone = String(d.phone ?? '').toLowerCase();
      return name.includes(q) || phone.includes(q);
    });
  }, [query]);

  const title = reassign ? 'Re-Assign Driver' : 'Assign Driver';

  const handleAssign = () => {
    if (!selectedId) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='xs'
      className='otd-assign-driver-modal'
      aria-labelledby='assign-driver-modal-title'
    >
      <div className='otd-assign-driver-modal__header'>
        <div>
          <h2 id='assign-driver-modal-title' className='otd-assign-driver-modal__title'>
            {title}
          </h2>
          {orderNo ? (
            <p className='otd-assign-driver-modal__subtitle'>Order #{orderNo}</p>
          ) : null}
        </div>
        <button
          type='button'
          className='otd-assign-driver-modal__close'
          onClick={onClose}
          aria-label='Close'
        >
          <X size={16} />
        </button>
      </div>

      <div className='otd-assign-driver-modal__search'>
        <input
          type='search'
          className='otd-assign-driver-modal__search-input'
          placeholder='Search drivers…'
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label='Search drivers'
        />
      </div>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {filteredDrivers.length === 0 ? (
          <p className='otd-assign-driver-modal__empty'>No drivers match your search.</p>
        ) : (
          <ul className='otd-assign-driver-modal__list' role='listbox' aria-label='Drivers'>
            {filteredDrivers.map((driver) => {
              const isSelected = selectedId === driver.id;
              const isOffline = driver.status === 'offline';
              return (
                <li key={driver.id} className='otd-assign-driver-modal__item' role='presentation'>
                  <button
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    disabled={isOffline}
                    className={`otd-assign-driver-modal__item-btn ${
                      isSelected ? 'otd-assign-driver-modal__item-btn--selected' : ''
                    }`}
                    onClick={() => setSelectedId(driver.id)}
                  >
                    <span className='otd-assign-driver-modal__avatar' aria-hidden>
                      {getInitials(driver.firstName, driver.lastName)}
                    </span>
                    <span className='otd-assign-driver-modal__info'>
                      <p className='otd-assign-driver-modal__name'>{getDriverDisplayName(driver)}</p>
                      <p className='otd-assign-driver-modal__meta'>
                        {driver.phone}
                        {driver.vehicle ? ` · ${driver.vehicle}` : ''}
                        {driver.activeOrders > 0
                          ? ` · ${driver.activeOrders} active order${driver.activeOrders === 1 ? '' : 's'}`
                          : ''}
                      </p>
                    </span>
                    <span
                      className={`otd-assign-driver-modal__badge otd-assign-driver-modal__badge--${driver.status}`}
                    >
                      {getDriverStatusLabel(driver.status)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>

      <p className='otd-assign-driver-modal__preview-note'>Preview only — assignment is not saved.</p>

      <div className='otd-assign-driver-modal__footer'>
        <button
          type='button'
          className='otd-assign-driver-modal__btn otd-assign-driver-modal__btn--cancel'
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type='button'
          className='otd-assign-driver-modal__btn otd-assign-driver-modal__btn--assign'
          disabled={!selectedId}
          onClick={handleAssign}
        >
          {title}
        </button>
      </div>
    </Dialog>
  );
}
